import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Users, Plus, Search, Upload, Download, Trash2, Edit3, ArrowRight, ArrowLeft, X, DollarSign, Mail, Send, ShoppingCart, FileText } from "lucide-react";
import { toast } from "react-toastify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "qualified", "proposal_sent", "negotiation", "won", "lost"];
const SOURCES = ["website", "referral", "social_media", "google_ads", "cold_outreach", "trade_show", "import", "other"];
const STATUS_COLORS = { new: "bg-blue-500/20 text-blue-400", contacted: "bg-cyan-500/20 text-cyan-400", qualified: "bg-yellow-500/20 text-yellow-400", proposal_sent: "bg-orange-500/20 text-orange-400", negotiation: "bg-purple-500/20 text-purple-400", won: "bg-green-500/20 text-green-400", lost: "bg-red-500/20 text-red-400" };
const SEGMENT_COLORS = { vip: "bg-amber-500/20 text-amber-400 border-amber-500/30", regular: "bg-blue-500/20 text-blue-400 border-blue-500/30", new: "bg-green-500/20 text-green-400 border-green-500/30", at_risk: "bg-red-500/20 text-red-400 border-red-500/30", high_value: "bg-purple-500/20 text-purple-400 border-purple-500/30", dormant: "bg-zinc-600/20 text-zinc-400 border-zinc-600/30" };

export default function LeadManagement({ initialContext = {} }) {
  const [leads, setLeads] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(initialContext.filterStatus || "");
  const [filterSource, setFilterSource] = useState(initialContext.filterSource || "");
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", message: "" });
  const [showEmail, setShowEmail] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", source: "website", status: "new", notes: "", estimated_value: 0 });
  const [importType, setImportType] = useState(null);
  const [tab, setTab] = useState(initialContext.tab || "leads");
  const [clients, setClients] = useState([]);

  const fetchLeads = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterSource) params.source = filterSource;
      const { data } = await axios.get(`${API}/crm/leads`, { params, withCredentials: true });
      setLeads(data.leads);
      setPipeline(data.pipeline);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterSource]);

  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/crm/clients`, { params: search ? { search } : {}, withCredentials: true });
      setClients(data.clients);
    } catch (err) { console.error(err); }
  }, [search]);

  useEffect(() => { fetchLeads(); fetchClients(); }, [fetchLeads, fetchClients]);

  // Apply initial filters from analytics click-through
  useEffect(() => {
    if (initialContext.filterStatus) setFilterStatus(initialContext.filterStatus);
    if (initialContext.filterSource) setFilterSource(initialContext.filterSource);
    if (initialContext.tab) setTab(initialContext.tab);
  }, [initialContext]);

  const saveLead = async () => {
    try {
      if (editing === "new") {
        await axios.post(`${API}/crm/leads`, form, { withCredentials: true });
      } else {
        await axios.put(`${API}/crm/leads/${editing}`, form, { withCredentials: true });
      }
      setEditing(null);
      setForm({ name: "", email: "", phone: "", company: "", source: "website", status: "new", notes: "", estimated_value: 0 });
      fetchLeads();
    } catch (err) { alert("Error saving lead"); }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await axios.delete(`${API}/crm/leads/${id}`, { withCredentials: true });
    fetchLeads();
  };

  const convertLead = async (id) => {
    if (!window.confirm("Convert this lead to a client?")) return;
    await axios.post(`${API}/crm/leads/${id}/convert`, {}, { withCredentials: true });
    fetchLeads(); fetchClients();
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const endpoint = importType === "clients" ? "clients" : "leads";
      const { data } = await axios.post(`${API}/crm/import/${endpoint}`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      alert(data.message);
      setImportType(null); fetchLeads(); fetchClients();
    } catch (err) { alert("Import failed"); }
    e.target.value = "";
  };

  const exportCSV = (type) => { window.open(`${API}/crm/export/${type}`, "_blank"); };

  const editLead = (lead) => {
    setForm({ name: lead.name, email: lead.email, phone: lead.phone || "", company: lead.company || "", source: lead.source || "website", status: lead.status, notes: lead.notes || "", estimated_value: lead.estimated_value || 0 });
    setEditing(lead.id);
  };

  // ── Open detail view ──
  const openDetail = async (item, type) => {
    setDetail({ id: item.id, type });
    try {
      const endpoint = type === "lead" ? "leads" : "clients";
      const { data } = await axios.get(`${API}/crm/${endpoint}/${item.id}`, { withCredentials: true });
      setDetailData(data);
    } catch (err) {
      setDetailData(item);
    }
  };

  // ── Send email from detail ──
  const sendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) { toast.error("Subject and message required"); return; }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/crm/clients/${detail.id}/email`, emailForm, { withCredentials: true });
      toast.success(`Email sent to ${detailData?.email}`);
      setShowEmail(false);
      setEmailForm({ subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally { setSendingEmail(false); }
  };

  /* ═══ DETAIL VIEW ═══ */
  if (detail && detailData) {
    const isClient = detail.type === "client";
    return (
      <div className="space-y-4" data-testid="contact-detail">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => { setDetail(null); setDetailData(null); setShowEmail(false); }} className="text-zinc-400 hover:text-white" data-testid="detail-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{detailData.name}</h2>
          {isClient && detailData.segments?.map(s => (
            <Badge key={s} className={`text-xs ${SEGMENT_COLORS[s] || "bg-zinc-700 text-zinc-400"}`}>{s.replace(/_/g,' ')}</Badge>
          ))}
          {!isClient && <Badge className={`text-xs ${STATUS_COLORS[detailData.status] || "bg-zinc-700"}`}>{(detailData.status || "").replace(/_/g,' ')}</Badge>}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 space-y-2">
              <h3 className="text-sm font-medium text-zinc-400">Contact Info</h3>
              <p className="text-white">{detailData.email}</p>
              {detailData.phone && <p className="text-zinc-400 text-sm">{detailData.phone}</p>}
              {detailData.company && <p className="text-zinc-500 text-sm">@ {detailData.company}</p>}
              {detailData.source && <Badge className="bg-zinc-800 text-zinc-500 text-xs">{(detailData.source || "").replace(/_/g,' ')}</Badge>}
            </CardContent>
          </Card>

          {isClient && detailData.metrics && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-medium text-zinc-400">Metrics</h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><p className="text-lg font-bold text-white">{detailData.metrics.total_orders}</p><p className="text-zinc-600 text-xs">Orders</p></div>
                  <div><p className="text-lg font-bold text-green-400">${detailData.metrics.total_spent}</p><p className="text-zinc-600 text-xs">LTV</p></div>
                  <div><p className="text-lg font-bold text-orange-400">${detailData.metrics.avg_order_value}</p><p className="text-zinc-600 text-xs">AOV</p></div>
                </div>
              </CardContent>
            </Card>
          )}

          {!isClient && (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 space-y-2">
                <h3 className="text-sm font-medium text-zinc-400">Lead Value</h3>
                <p className="text-2xl font-bold text-green-400">${detailData.estimated_value || 0}</p>
                <p className="text-zinc-600 text-xs">Created {new Date(detailData.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          )}

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-2">Quick Actions</h3>
              <div className="space-y-2">
                <Button onClick={() => setShowEmail(true)} className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm" data-testid="send-email-btn">
                  <Mail className="w-4 h-4 mr-2" /> Send Email
                </Button>
                {!isClient && (
                  <Button onClick={() => convertLead(detail.id)} variant="outline" className="w-full border-green-500/30 text-green-400 text-sm" data-testid="convert-btn">
                    <ArrowRight className="w-4 h-4 mr-2" /> Convert to Client
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Email compose */}
        {showEmail && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4 sm:p-6 space-y-3">
              <h3 className="text-white font-bold flex items-center gap-2"><Send className="w-4 h-4 text-orange-400" /> Send Email to {detailData.name}</h3>
              <Input placeholder="Subject *" value={emailForm.subject} onChange={e => setEmailForm({...emailForm, subject: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" data-testid="email-subject" />
              <Textarea placeholder="Message *" value={emailForm.message} onChange={e => setEmailForm({...emailForm, message: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white" rows={6} data-testid="email-message" />
              <div className="flex gap-2">
                <Button onClick={() => setShowEmail(false)} variant="outline" className="border-zinc-700 text-zinc-400">Cancel</Button>
                <Button onClick={sendEmail} disabled={sendingEmail} className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="send-email-submit">
                  {sendingEmail ? "Sending..." : "Send Email"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {detailData.notes && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-2">Notes</h3>
              <p className="text-zinc-300 text-sm whitespace-pre-wrap">{detailData.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Order history (clients) */}
        {isClient && detailData.orders?.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Order History</h3>
              <div className="space-y-2">
                {detailData.orders.map(o => (
                  <div key={o.id || o.order_number} className="flex flex-col sm:flex-row sm:items-center justify-between bg-zinc-800 rounded-lg p-3 gap-2">
                    <div>
                      <p className="text-white font-medium text-sm">{o.order_number}</p>
                      <p className="text-zinc-500 text-xs">{new Date(o.created_at).toLocaleDateString()} &middot; {o.items?.length || 0} items</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={o.payment_status === "paid" ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>{o.payment_status}</Badge>
                      <span className="text-white font-bold">${o.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  /* ═══ EDIT FORM ═══ */
  if (editing) {
    return (
      <div className="space-y-4" data-testid="lead-form">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-white">{editing === "new" ? "Add Lead" : "Edit Lead"}</h2>
          <Button variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 shrink-0"><X className="w-5 h-5" /></Button>
        </div>
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-zinc-400 text-xs">Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="lead-name" /></div>
              <div><Label className="text-zinc-400 text-xs">Email *</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" data-testid="lead-email" /></div>
              <div><Label className="text-zinc-400 text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
              <div><Label className="text-zinc-400 text-xs">Company</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
              <div><Label className="text-zinc-400 text-xs">Source</Label>
                <Select value={form.source} onValueChange={v => setForm({...form, source: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-400 text-xs">Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-400 text-xs">Estimated Value ($)</Label><Input type="number" value={form.estimated_value} onChange={e => setForm({...form, estimated_value: parseFloat(e.target.value) || 0})} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
            </div>
            <div><Label className="text-zinc-400 text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" rows={3} /></div>
            <Button onClick={saveLead} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="save-lead-btn">Save Lead</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ═══ LIST VIEW ═══ */
  return (
    <div className="space-y-4" data-testid="lead-management">
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        {[{id: "leads", label: "Leads"}, {id: "clients", label: "Clients"}].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setFilterStatus(""); setFilterSource(""); }} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${tab === t.id ? 'bg-zinc-800 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>{t.label}</button>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Users className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> {tab === "leads" ? "Leads" : "Clients"}
        </h2>
        {tab === "leads" && (
          <Button onClick={() => { setEditing("new"); setForm({ name: "", email: "", phone: "", company: "", source: "website", status: "new", notes: "", estimated_value: 0 }); }} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="add-lead-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Lead
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[140px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="bg-zinc-800 border-zinc-700 text-white pl-9" data-testid="search-input" />
        </div>
        {tab === "leads" && (
          <>
            <Select value={filterStatus || "all"} onValueChange={v => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-36"><SelectValue placeholder="All statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterSource || "all_sources"} onValueChange={v => setFilterSource(v === "all_sources" ? "" : v)}>
              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white w-36"><SelectValue placeholder="All sources" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all_sources">All sources</SelectItem>
                {SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}
              </SelectContent>
            </Select>
          </>
        )}
        <div className="relative">
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={() => setImportType(importType ? null : tab)} data-testid="import-btn"><Upload className="w-4 h-4 mr-1" /> Import</Button>
          {importType && <input type="file" accept=".csv" onChange={handleImport} className="absolute inset-0 opacity-0 cursor-pointer" />}
        </div>
        <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={() => exportCSV(tab)} data-testid="export-btn"><Download className="w-4 h-4 mr-1" /> Export</Button>
        <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={() => window.open(`${API}/crm/template/${tab}`, "_blank")} data-testid="download-template-btn"><FileText className="w-4 h-4 mr-1" /> CSV Template</Button>
      </div>

      {/* Pipeline chips */}
      {tab === "leads" && (
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(s => (
            <Badge key={s} className={`cursor-pointer whitespace-nowrap ${STATUS_COLORS[s]} ${filterStatus === s ? 'ring-2 ring-orange-400' : ''}`} onClick={() => setFilterStatus(filterStatus === s ? "" : s)}>
              {s.replace(/_/g,' ')} ({pipeline[s] || 0})
            </Badge>
          ))}
        </div>
      )}

      {/* List */}
      {loading ? <div className="text-zinc-500 text-center py-8">Loading...</div> : (
        <div className="space-y-2">
          {(tab === "leads" ? leads : clients).length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800"><CardContent className="py-12 text-center"><Users className="w-10 h-10 text-zinc-700 mx-auto mb-3" /><p className="text-zinc-400">No {tab} found.</p></CardContent></Card>
          ) : (tab === "leads" ? leads : clients).map(item => (
            <Card key={item.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition">
              <CardContent className="p-4">
                <div className="flex items-start sm:items-center gap-3">
                  <button onClick={() => openDetail(item, tab === "leads" ? "lead" : "client")} className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center text-orange-400 font-bold text-lg shrink-0 hover:bg-zinc-700 transition" data-testid={`open-${item.id}`}>
                    {(item.name || "?")[0].toUpperCase()}
                  </button>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(item, tab === "leads" ? "lead" : "client")}>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <p className="text-white font-medium hover:text-orange-400 transition">{item.name}</p>
                      {item.company && <span className="text-zinc-600 text-xs">@ {item.company}</span>}
                      {tab === "leads" && <Badge className={`text-[10px] ${STATUS_COLORS[item.status] || "bg-zinc-700 text-zinc-400"}`}>{(item.status || "").replace(/_/g,' ')}</Badge>}
                      {tab === "clients" && item.segments?.map(s => (
                        <Badge key={s} className={`text-[10px] ${SEGMENT_COLORS[s] || "bg-zinc-700 text-zinc-400"}`}>{s.replace(/_/g,' ')}</Badge>
                      ))}
                    </div>
                    <p className="text-zinc-500 text-sm truncate">{item.email}{item.phone ? ` | ${item.phone}` : ''}</p>
                    {tab === "leads" && (
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {item.estimated_value > 0 && <span className="text-green-400 text-xs font-medium flex items-center"><DollarSign className="w-3 h-3" />{item.estimated_value}</span>}
                        <Badge className="bg-zinc-800 text-zinc-500 text-[10px]">{(item.source || "").replace(/_/g,' ')}</Badge>
                      </div>
                    )}
                    {tab === "clients" && item.metrics && (
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        <span className="text-zinc-500 text-xs">{item.metrics?.total_orders || 0} orders</span>
                        <span className="text-green-400 text-xs font-medium">${item.metrics?.total_spent || item.total_spent || 0}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {tab === "leads" && <Button variant="ghost" size="sm" onClick={() => convertLead(item.id)} className="text-green-400 hover:text-green-300" title="Convert to client"><ArrowRight className="w-4 h-4" /></Button>}
                    {tab === "leads" && <Button variant="ghost" size="sm" onClick={() => editLead(item)} className="text-zinc-400 hover:text-white"><Edit3 className="w-4 h-4" /></Button>}
                    <Button variant="ghost" size="sm" onClick={() => tab === "leads" ? deleteLead(item.id) : null} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
